import {
  DesignTokenSizeEnum,
  Text,
  TokenTextAppearance,
  TextTag,
} from '@volkswagen-onehub/components-core';
import { useMemo, useRef, useState } from 'react';
import { useTribalContext } from '@/context/tribal';
import useResizeResponsive from '@/hooks/useResizeResponsive';
import AutoScrollContainer from '@/modules/components/AutoScrollContainer/AutoScrollContainer';
import Box from '@/modules/components/Box/Box';
import CTAModal from '@/modules/components/CTAModal/CTAModal';
import CardFeaturedItem, {
  CardFeaturedItemProps,
} from '@/modules/components/CardFeaturedItem/CardFeaturedItem';
import useFeaturedImage from '@/modules/components/CardFeaturedItem/useFeaturedImages';
import { isDesktop } from '@/modules/utils/breakpoints';
import { LimitContent } from '@/modules/components';
import { FeatureStatus } from '@/hooks/useFeatures';
import {
  performVWBasicLayerClose,
  performVWBasicLayerLoad,
  performVWBasicLinkClick,
} from '@/analytics/events';
import { SectionNames } from '@/analytics/sectionNames';
import useGlobalStore from '@/stores/globalStore';

interface FeatureCardsProps {
  title: string;
  items: (CardFeaturedItemProps & any)[];
  activeSlideIndex: number;
  sectionName?: SectionNames;
}

const FeatureCards = (props: FeatureCardsProps) => {
  const { contentManager, analyticsManager, analyticsBuilder } =
    useTribalContext();
  const { configurations, retailers } = useGlobalStore((state) => state.app);
  const { title, items, activeSlideIndex, sectionName } = props;
  const [readMores, setReadMores] = useState<number[]>([]);
  const readMeRef = useRef<number[]>([]);

  const addReadMore = (indices: number[]) => {
    setReadMores(indices);
  };

  const readMoreLabel = contentManager.get('ctas-read-more');
  const isDesktopWidth = useResizeResponsive(isDesktop);

  const maskSize = useMemo(
    () => (isDesktopWidth ? 'medium' : 'small'),
    [isDesktopWidth]
  );

  const images = useFeaturedImage(
    items.map(({ imageOptions }) => imageOptions)
  );

  const analyticsCommonFields = {
    sectionName,
    retailers,
    analyticsManager,
    analyticsBuilder,
  };

  const analyticsLinkFn = (label: string, index: number) => {
    performVWBasicLinkClick({
      label,
      configuration: configurations[index],
      ...analyticsCommonFields,
    });
  };

  const analyticsOpenModalFn = (index: number) =>
    performVWBasicLayerLoad({
      configuration: configurations[index],
      ...analyticsCommonFields,
    });

  const analyticsCloseModalFn = (index: number) =>
    performVWBasicLayerClose({
      configuration: configurations[index],
      ...analyticsCommonFields,
    });

  const cards = useMemo(
    () =>
      items.map((itemProps, i: number) => {
        const {
          title,
          description,
          body,
          availability,
          secondaryLink,
          id,
          ...restProps
        } = itemProps;

        let additionalProps: any = {};

        if (availability === FeatureStatus.NOT_AVAILABLE) {
          additionalProps.body = contentManager.get(
            'configuration-not-available'
          );
          additionalProps.inactive = true;
          additionalProps.price = undefined;
        }

        return (
          <Box
            key={`${props.title}${id}${i}`}
            as={CardFeaturedItem}
            width="100%"
            maskSize={maskSize}
            body={
              <LimitContent
                maxLines={2}
                onMaxLinesReached={() => {
                  addReadMore([...readMeRef.current, i]);
                }}
              >
                {body}
              </LimitContent>
            }
            {...restProps}
            {...additionalProps}
            decoratorImgContent={<img src={images[i]} />}
            links={
              <>
                {(title !== description || readMores.includes(i)) && (
                  <CTAModal
                    tag="button"
                    modalProps={{ title, description }}
                    onClick={() => analyticsLinkFn(readMoreLabel, i)}
                    openCallback={() => analyticsOpenModalFn(i)}
                    closeCallback={() => analyticsCloseModalFn(i)}
                  >
                    {readMoreLabel}
                  </CTAModal>
                )}
                {secondaryLink && (
                  <CTAModal
                    modalProps={{
                      title,
                      description: title !== description ? description : null,
                      body: secondaryLink.modalContent,
                    }}
                    tag="button"
                    onClick={() => analyticsLinkFn(secondaryLink.label, i)}
                    openCallback={() => analyticsOpenModalFn(i)}
                    closeCallback={() => analyticsCloseModalFn(i)}
                  >
                    {secondaryLink.label}
                  </CTAModal>
                )}
              </>
            }
          />
        );
      }),
    [items, images]
  );

  return (
    <Box width="100%">
      <Box
        as={Text}
        tag={TextTag.h4}
        appearance={TokenTextAppearance.headline200}
        mb={DesignTokenSizeEnum.static200}
        b960={{ mb: DesignTokenSizeEnum.static350 }}
      >
        {title}
      </Box>
      <AutoScrollContainer activeSlideIndex={activeSlideIndex}>
        {cards}
      </AutoScrollContainer>
    </Box>
  );
};

export default FeatureCards;
